import React from "react";
import { XMarkIcon, ClockIcon, UserIcon, ChatBubbleLeftEllipsisIcon, CalendarIcon } from "@heroicons/react/24/outline";

const StatusHistoryModal = ({ isOpen, onClose, client }) => {
  if (!isOpen) return null;

  const statusHistory = client?.statusHistory || [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'discharged':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'recall':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'deceased':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'other':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatRecallDate = (date) => {
    return new Date(date).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Sort history by date, newest first
  const sortedHistory = [...statusHistory].sort((a, b) => 
    new Date(b.changedAt) - new Date(a.changedAt)
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-sm shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Status History
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {client?.firstName} {client?.lastName} (ID: {client?.clientId})
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-sm transition-colors">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {sortedHistory.length === 0 ? (
            <div className="text-center py-8">
              <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-sm">
                No status changes recorded for this client.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedHistory.map((entry, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-sm p-4 bg-gray-50">
                  {/* Status and Date Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border capitalize ${getStatusColor(entry.status)}`}>
                        {entry.status}
                      </span>
                      {index === 0 && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-sm border border-blue-200">
                          Current
                        </span>
                      )}
                    </div>
                    <div className="flex items-center text-xs text-gray-500">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {formatDate(entry.changedAt)}
                    </div>
                  </div>

                  {/* User Information */}
                  <div className="flex items-center text-sm text-gray-700 mb-2">
                    <UserIcon className="h-4 w-4 mr-2 text-gray-500" />
                    <span>
                      Changed by:{" "}
                      <span className="font-medium">
                        {entry.changedBy?.firstName && entry.changedBy?.lastName
                          ? `${entry.changedBy.firstName} ${entry.changedBy.lastName}`
                          : entry.changedBy?.email || "Unknown User"}
                      </span>
                    </span>
                  </div>

                  {/* Recall Date */}
                  {entry.recallDate && (
                    <div className="flex items-center text-sm text-gray-700 mb-2">
                      <CalendarIcon className="h-4 w-4 mr-2 text-orange-500" />
                      <span>
                        Recall Date:{" "}
                        <span className="font-medium text-orange-700">
                          {formatRecallDate(entry.recallDate)}
                        </span>
                      </span>
                    </div>
                  )}

                  {/* Comment */}
                  {entry.comment && (
                    <div className="flex items-start text-sm text-gray-700">
                      <ChatBubbleLeftEllipsisIcon className="h-4 w-4 mr-2 text-gray-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="text-gray-500">Comment:</span>
                        <p className="font-medium mt-1 text-gray-800">
                          {entry.comment}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatusHistoryModal;